import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as generateUniqueId } from "uuid";
import type {
    ContactInformationValues,
    StatementOfHealthValues,
    CoverageReplacementValues,
    PolicyBeneficiaryValues,
    SecondaryAddresseeValues,
} from "types/Card";
import type { OfferData, LandingPageData, CampaignData, LoyaltyLandingParams } from "types/OfferData";
import type { DTOCoverageOption } from "types/CoverageOptions";
import { getFirst30CharsOfSessionId } from "utils/sessionIdUtils";

export interface ContactData {
    contactInformation: ContactInformationValues | null;
    statementOfHealth: StatementOfHealthValues | null;
    coverageReplacement: CoverageReplacementValues | null;
    policyBeneficiary: PolicyBeneficiaryValues | null;
    secondaryAddressee: SecondaryAddresseeValues | null;
    policyNumber: string | null;
    hannoverId: string | null;
    clubCode: string | null;
}

export interface PaymentInformationData {
    paymentToken: string;
    paymentType: "CC" | "ET";
    nameOnAccount?: string;
    lastFourDigits?: string;
}

/**
 * MLTA benefit data from API response
 * Represents a single tier's coverage details
 */
export interface MLTABenefit {
    tier: string;
    level: number;
    airline: number;
    common: number;
    lossOfLife: number;
    hospital: number;
    emergencyRoom: number;
    allAccident: number;
    individual: number;
    family: number;
    correctKeycode: string;
}

export interface QuoteData {
    state: string;
    email?: string;
    dobMonth: string;
    dobDay: string;
    dobYear: string;
    applicantType: "member" | "spouse";
    zipCode?: string;
    selectedCoverageAmount?: number;
    selectedCoveragePremium?: number;
    hasUsedNicotineLastYear?: boolean;
    gender?: "male" | "female"; // Optional for MLTA (not collected)
    coverageOptions?: DTOCoverageOption[];
    // MLTA-specific fields
    mltaBenefits?: MLTABenefit[];
    selectedTier?: "Value" | "Economy" | "Deluxe" | "Ultra" | "Superior" | "Premier";
    selectedCoverageType?: "Individual" | "Family";
    clubCode?: string;
    planCode?: string;
    keyCode?: string;
    membershipLevel?: number;
    // GIWL-specific fields
    isMember?: boolean;
    policyType?: string;
}

export interface ApplicationState {
    offerData: OfferData | null;
    landingPageData: LandingPageData | null;
    quoteData: QuoteData | null;
    contactData: ContactData | null;
    paymentInformationData: PaymentInformationData | null;
    needsReview: boolean;
    isContactDataComplete: boolean;
    isPaymentInformationComplete: boolean;
    currentStep: "contact" | "payment" | "review";
    sessionId: string | null;
    saveContId: string | null;
    isResumedApplication: boolean;
    saveContSessionCreatedFired: boolean;
    // Landing-params key the GIWL hydration effect last applied. Persisted so
    // navigating back to the contact page (or refreshing) with the same landing
    // params in the URL doesn't reset the in-progress application. Optional so
    // existing state fixtures need not declare it — only the GIWL flow uses it,
    // and initialState always seeds it at runtime.
    giwlHydratedParamsKey?: string | null;
    isApplicationSubmitted: boolean;
}

/**
 * Helper to generate new sessionId and saveContId
 */
const generateSessionIds = () => {
    const sessionId = generateUniqueId();
    const saveContId = getFirst30CharsOfSessionId(sessionId) ?? null;
    return { sessionId, saveContId };
};

const { sessionId: initialSessionId, saveContId: initialSaveContId } = generateSessionIds();

const initialState: ApplicationState = {
    offerData: null,
    landingPageData: null,
    quoteData: null,
    contactData: null,
    paymentInformationData: null,
    needsReview: false,
    isContactDataComplete: false,
    isPaymentInformationComplete: false,
    currentStep: "contact",
    sessionId: initialSessionId,
    saveContId: initialSaveContId,
    isResumedApplication: false,
    saveContSessionCreatedFired: false,
    giwlHydratedParamsKey: null,
    isApplicationSubmitted: false,
};

export const applicationSlice = createSlice({
    name: "application",
    initialState,
    reducers: {
        // Save Offer data
        saveOfferData: (state, action: PayloadAction<OfferData>) => {
            state.offerData = action.payload;
            return state;
        },

        // Update specific fields in offer data
        updateOfferData: (state, action: PayloadAction<Partial<OfferData>>) => {
            if (state.offerData) {
                state.offerData = { ...state.offerData, ...action.payload };
            }
        },

        // Save landing page data (campaign params, loyalty params)
        saveLandingPageData: (state, action: PayloadAction<LandingPageData>) => {
            state.landingPageData = action.payload;
        },

        // Update campaign data
        updateCampaignData: (state, action: PayloadAction<Partial<CampaignData>>) => {
            state.landingPageData ??= { campaign: {} };
            state.landingPageData.campaign = { ...state.landingPageData.campaign, ...action.payload };
        },

        // Update loyalty params (MLTA-specific)
        updateLoyaltyParams: (state, action: PayloadAction<Partial<LoyaltyLandingParams>>) => {
            state.landingPageData ??= { campaign: {} };
            state.landingPageData.loyaltyParams = {
                ...state.landingPageData.loyaltyParams,
                ...action.payload,
            };
        },

        // Record the landing-params key the GIWL hydration effect applied, so the
        // same params re-appearing in the URL don't trigger another reset+hydrate
        setGiwlHydratedParamsKey: (state, action: PayloadAction<string>) => {
            state.giwlHydratedParamsKey = action.payload;
        },

        // Set parameter errors
        setParameterErrors: (state, action: PayloadAction<string[]>) => {
            state.landingPageData ??= { campaign: {} };
            state.landingPageData.parameterErrors = action.payload;
        },

        // Save DTO Quote data
        saveQuoteData: (state, action: PayloadAction<QuoteData>) => {
            state.quoteData = action.payload;
            return state;
        },

        // Update specific fields in quote data
        updateQuoteData: (state, action: PayloadAction<Partial<QuoteData>>) => {
            if (state.quoteData) {
                state.quoteData = { ...state.quoteData, ...action.payload };
            }
        },

        // Clear quote data
        clearQuoteData: (state) => {
            state.quoteData = null;
        },

        // Save Contact Information data
        saveContactInformation: (
            state,
            action: PayloadAction<{
                contactData: ContactData;
                needsReview: boolean;
            }>
        ) => {
            state.contactData = action.payload.contactData;
            state.needsReview = action.payload.needsReview;
            state.isContactDataComplete = true;
            state.currentStep = "payment";
        },

        // Persist the backend-minted policy number onto the saved contact data.
        // GIWL receives its policyNumber in the submit response (not during the contact
        // step like DTO/MLTA), so the success page reads it from here.
        setPolicyNumber: (state, action: PayloadAction<string>) => {
            if (state.contactData) {
                state.contactData.policyNumber = action.payload;
            }
        },

        // Save Payment Information data
        savePaymentInformation: (state, action: PayloadAction<PaymentInformationData>) => {
            state.paymentInformationData = action.payload;
            state.isPaymentInformationComplete = true;
            state.currentStep = "review";
        },

        // Update current step
        setCurrentStep: (state, action: PayloadAction<"contact" | "payment" | "review">) => {
            state.currentStep = action.payload;
        },

        // Reset the entire DTO application state (preserves landingPageData from URL params)
        // By default generates NEW sessionId/saveContId to start a fresh session.
        // Pass { preserveSessionId: true } to keep the current session IDs
        // (e.g. when clearing data mid-flow without starting a new session).
        resetApplication: {
            reducer: (state, action: PayloadAction<{ preserveSessionId: boolean }>) => {
                const sessionIds = action.payload.preserveSessionId
                    ? { sessionId: state.sessionId, saveContId: state.saveContId }
                    : generateSessionIds();
                return {
                    ...initialState,
                    landingPageData: state.landingPageData,
                    ...sessionIds,
                };
            },
            prepare: (options?: { preserveSessionId: boolean }) => ({
                payload: { preserveSessionId: options?.preserveSessionId ?? false },
            }),
        },

        // Update a specific section of contact information
        updateContactInformationSection: (
            state,
            action: PayloadAction<{
                section: keyof ContactData;
                data: any;
            }>
        ) => {
            state.contactData ??= {
                contactInformation: null,
                statementOfHealth: null,
                coverageReplacement: null,
                policyBeneficiary: null,
                secondaryAddressee: null,
                policyNumber: null,
                hannoverId: null,
                clubCode: null,
            };
            state.contactData[action.payload.section] = action.payload.data;
        },

        // Individual card save actions for auto-save on blur
        saveContactInformationCard: (state, action: PayloadAction<ContactInformationValues>) => {
            state.contactData ??= {
                contactInformation: null,
                statementOfHealth: null,
                coverageReplacement: null,
                policyBeneficiary: null,
                secondaryAddressee: null,
                policyNumber: null,
                hannoverId: null,
                clubCode: null,
            };
            state.contactData.contactInformation = action.payload;
        },

        saveStatementOfHealthCard: (state, action: PayloadAction<StatementOfHealthValues>) => {
            state.contactData ??= {
                contactInformation: null,
                statementOfHealth: null,
                coverageReplacement: null,
                policyBeneficiary: null,
                secondaryAddressee: null,
                policyNumber: null,
                hannoverId: null,
                clubCode: null,
            };
            state.contactData.statementOfHealth = action.payload;
        },

        saveCoverageReplacementCard: (state, action: PayloadAction<CoverageReplacementValues>) => {
            state.contactData ??= {
                contactInformation: null,
                statementOfHealth: null,
                coverageReplacement: null,
                policyBeneficiary: null,
                secondaryAddressee: null,
                policyNumber: null,
                hannoverId: null,
                clubCode: null,
            };
            state.contactData.coverageReplacement = action.payload;
        },

        savePolicyBeneficiaryCard: (state, action: PayloadAction<PolicyBeneficiaryValues>) => {
            state.contactData ??= {
                contactInformation: null,
                statementOfHealth: null,
                coverageReplacement: null,
                policyBeneficiary: null,
                secondaryAddressee: null,
                policyNumber: null,
                hannoverId: null,
                clubCode: null,
            };
            state.contactData.policyBeneficiary = action.payload;
        },

        saveSecondaryAddresseeCard: (state, action: PayloadAction<SecondaryAddresseeValues>) => {
            state.contactData ??= {
                contactInformation: null,
                statementOfHealth: null,
                coverageReplacement: null,
                policyBeneficiary: null,
                secondaryAddressee: null,
                policyNumber: null,
                hannoverId: null,
                clubCode: null,
            };
            state.contactData.secondaryAddressee = action.payload;
        },

        // Set needsReview flag
        setNeedsReview: (state, action: PayloadAction<boolean>) => {
            state.needsReview = action.payload;
        },

        // Update offer availability
        updateOfferAvailability: (
            state,
            action: PayloadAction<{
                memberOfferAvailable?: boolean;
                spouseOfferAvailable?: boolean;
            }>
        ) => {
            if (state.offerData) {
                if (action.payload.memberOfferAvailable !== undefined) {
                    state.offerData.memberOfferAvailable = action.payload.memberOfferAvailable;
                }
                if (action.payload.spouseOfferAvailable !== undefined) {
                    state.offerData.spouseOfferAvailable = action.payload.spouseOfferAvailable;
                }
            }
        },

        // Reset state while preserving offerData.
        // By default generates NEW sessionId/saveContId (e.g. starting a new flow).
        // Pass { preserveSessionId: true } to keep the current session IDs
        // (e.g. when switching tabs mid-flow).
        resetApplicationKeepOfferData: {
            reducer: (state, action: PayloadAction<{ preserveSessionId: boolean }>) => {
                const sessionIds = action.payload.preserveSessionId
                    ? { sessionId: state.sessionId, saveContId: state.saveContId }
                    : generateSessionIds();
                return {
                    ...initialState,
                    offerData: state.offerData,
                    ...sessionIds,
                };
            },
            prepare: (options?: { preserveSessionId: boolean }) => ({
                payload: { preserveSessionId: options?.preserveSessionId ?? false },
            }),
        },

        // Hydrate contact data (for pre-filling forms from backend or auto-save)
        // This does NOT mark the data as complete or change the current step
        hydrateContactData: (state, action: PayloadAction<ContactData>) => {
            state.contactData = action.payload;
        },

        // Save session ID and saveContId
        saveSessionId: (
            state,
            action: PayloadAction<{
                sessionId: string;
                saveContId: string;
            }>
        ) => {
            state.sessionId = action.payload.sessionId;
            state.saveContId = action.payload.saveContId;
        },

        markResumedApplication: (state) => {
            state.isResumedApplication = true;
        },

        markSessionCreatedFired: (state) => {
            state.saveContSessionCreatedFired = true;
        },

        // Marks the application as terminally submitted (a decision page was reached).
        // Form pages guard on this flag so back-navigation cannot resubmit.
        // Cleared by resetApplication / resetApplicationKeepOfferData when a new
        // application flow starts.
        markApplicationSubmitted: (state) => {
            state.isApplicationSubmitted = true;
        },
    },
});

export const {
    saveOfferData,
    updateOfferData,
    saveLandingPageData,
    updateCampaignData,
    updateLoyaltyParams,
    setGiwlHydratedParamsKey,
    setParameterErrors,
    saveQuoteData,
    updateQuoteData,
    clearQuoteData,
    saveContactInformation,
    setPolicyNumber,
    savePaymentInformation,
    setCurrentStep,
    resetApplication,
    updateContactInformationSection,
    saveContactInformationCard,
    saveStatementOfHealthCard,
    saveCoverageReplacementCard,
    savePolicyBeneficiaryCard,
    saveSecondaryAddresseeCard,
    setNeedsReview,
    updateOfferAvailability,
    resetApplicationKeepOfferData,
    hydrateContactData,
    saveSessionId,
    markResumedApplication,
    markSessionCreatedFired,
    markApplicationSubmitted,
} = applicationSlice.actions;

export default applicationSlice.reducer;
